<template>
	<div>
		<v-btn
			icon
			small
			class="app-bar-search-toggler"
			@click="openSearch"
		>
			<v-icon>
				{{ icons.mdiMagnify }}
			</v-icon>
		</v-btn>

		<v-dialog
			:value="shallShowFullSearchLocal"
			hide-overlay
			persistent
		/>
		<v-expand-transition>
			<div
				v-show="shallShowFullSearchLocal"
				class="app-bar-autocomplete-box"
			>
				<!-- Search Input -->
				<div class="d-flex align-center search-input-row">
					<v-icon
						size="22"
						class="mr-2"
					>
						{{ icons.mdiMagnify }}
					</v-icon>
					<input
						ref="searchInput"
						v-model="searchQuery"
						class="search-native-input"
						placeholder="Buscar em todas as plataformas..."
						@keyup.esc="closeSearch"
					>
					<v-progress-circular
						v-if="isLoading"
						indeterminate
						size="20"
						width="2"
						class="mr-2"
					/>
					<v-icon @click="closeSearch">
						{{ icons.mdiClose }}
					</v-icon>
				</div>

				<!-- Results Dropdown -->
				<div
					v-if="searchQuery && searchQuery.length >= 2"
					class="search-results-dropdown"
				>
					<!-- Filter Chips -->
					<div
						v-if="availableTabs.length > 1"
						class="filter-chips pa-2"
					>
						<v-chip
							small
							class="mr-1 mb-1"
							:color="activeFilter === 'all' ? 'primary' : ''"
							:text-color="activeFilter === 'all' ? 'white' : ''"
							@click="activeFilter = 'all'"
						>
							Todos
						</v-chip>
						<v-chip
							v-for="tab in availableTabs"
							:key="tab"
							small
							class="mr-1 mb-1"
							:color="activeFilter === tab ? 'primary' : ''"
							:text-color="activeFilter === tab ? 'white' : ''"
							@click="activeFilter = tab"
						>
							<v-icon
								left
								x-small
							>
								{{ getTypeConfig(tab).icon }}
							</v-icon>
							{{ getTypeConfig(tab).label }}
							<span
								v-if="paginationMeta[tab]"
								class="ml-1 text-caption"
							>({{ paginationMeta[tab].total }})</span>
						</v-chip>
					</div>

					<!-- Grouped Results -->
					<div
						v-if="hasResults"
						class="results-container"
					>
						<template v-for="(items, type) in filteredResults">
							<div
								v-if="items && items.length"
								:key="type"
								class="result-group"
							>
								<div class="result-group-header text-overline px-4 pt-2 pb-1">
									<v-icon
										x-small
										class="mr-1"
									>
										{{ getTypeConfig(type).icon }}
									</v-icon>
									{{ getTypeConfig(type).label }}
									<span class="grey--text text-caption ml-1">({{ paginationMeta[type] ? paginationMeta[type].total : items.length }})</span>
								</div>
								<v-list
									dense
									class="py-0"
								>
									<v-list-item
										v-for="(item, idx) in items"
										:key="item.id || idx"
										class="search-result-item"
										:class="{ 'has-route': !!getTypeConfig(type).getRoute(item) }"
										@click="navigateToItem(type, item)"
									>
										<v-list-item-avatar
											size="32"
											:tile="type !== 'users'"
											class="my-1 mr-3"
										>
											<v-img
												v-if="getTypeConfig(type).getThumbnail(item)"
												:src="getTypeConfig(type).getThumbnail(item)"
											/>
											<v-icon v-else>
												{{ (type === 'nav_modules' && item.icon) ? item.icon : getTypeConfig(type).icon }}
											</v-icon>
										</v-list-item-avatar>
										<v-list-item-content>
											<v-list-item-title class="text-sm">
												{{ getTypeConfig(type).getDisplayName(item) }}
											</v-list-item-title>
											<v-list-item-subtitle
												v-if="getTypeConfig(type).getSubtitle(item)"
												class="text-xs"
											>
												{{ getTypeConfig(type).getSubtitle(item) }}
											</v-list-item-subtitle>
										</v-list-item-content>
										<v-list-item-action>
											<v-chip
												v-if="getTypeConfig(type).source"
												x-small
												outlined
												class="source-chip"
											>
												{{ getTypeConfig(type).source }}
											</v-chip>
											<v-chip
												v-else-if="type === 'nav_modules'"
												x-small
												outlined
												color="primary"
												class="source-chip"
											>
												{{ item.platform }}
											</v-chip>
										</v-list-item-action>
										<v-list-item-action
											v-if="getTypeConfig(type).getRoute(item)"
											class="ml-0"
										>
											<v-icon small>
												{{ icons.mdiChevronRight }}
											</v-icon>
										</v-list-item-action>
										<v-list-item-action
											v-else
											class="ml-0"
										>
											<v-chip
												x-small
												outlined
												disabled
											>
												sem edição
											</v-chip>
										</v-list-item-action>
									</v-list-item>
								</v-list>
								<!-- Load More Button -->
								<div
									v-if="paginationMeta[type] && paginationMeta[type].hasMore"
									class="load-more-row"
								>
									<v-btn
										text
										small
										color="primary"
										:loading="loadingMore[type]"
										@click="loadMore(type)"
									>
										<v-icon
											left
											small
										>
											{{ icons.mdiChevronDown }}
										</v-icon>
										Carregar mais {{ getTypeConfig(type).label }}
									</v-btn>
								</div>
							</div>
						</template>
					</div>

					<!-- No Results -->
					<div
						v-else-if="!isLoading"
						class="pa-4 text-center grey--text"
					>
						Sem resultados para "{{ searchQuery }}"
					</div>
				</div>
			</div>
		</v-expand-transition>
	</div>
</template>

<script>
import { mdiMagnify, mdiClose, mdiChevronRight, mdiChevronDown } from '@mdi/js'
import { useVModel } from '@vueuse/core'
import { ref, reactive, watch, computed, nextTick, getCurrentInstance } from 'vue'
import { useRouter } from '@core/utils'
import store from '@/store'
import { globalSearchAll, loadMoreResults } from '@/services/community/search.service'
import { searchNavItems } from '@/utils/navSearch'
import { SEARCH_TYPE_CONFIG } from './searchConfig'

export default {
	props: {
		shallShowFullSearch: {
			type: Boolean,
			default: false,
		},
	},
	setup(props, { emit }) {
		const shallShowFullSearchLocal = useVModel(props, 'shallShowFullSearch', emit)
		const searchQuery = ref('')
		const searchResults = ref({})
		const paginationMeta = ref({})
		const isLoading = ref(false)
		const loadingMore = reactive({})
		const activeFilter = ref('all')
		const searchInput = ref(null)
		const { router } = useRouter()
		const instance = getCurrentInstance()

		let debounceTimer = null

		const getTypeConfig = type => SEARCH_TYPE_CONFIG[type] || {
			label: type,
			icon: mdiMagnify,
			source: '',
			getDisplayName: item => item.name || item.title || '',
			getSubtitle: () => '',
			getThumbnail: () => null,
			getRoute: () => null,
		}

		const availableTabs = computed(() => {
			return Object.keys(searchResults.value).filter(
				key => searchResults.value[key] && searchResults.value[key].length > 0
			)
		})

		const filteredResults = computed(() => {
			if (activeFilter.value === 'all') {
				const filtered = {}
				for (const key of availableTabs.value) {
					filtered[key] = searchResults.value[key]
				}
				return filtered
			}
			return { [activeFilter.value]: searchResults.value[activeFilter.value] || [] }
		})

		const hasResults = computed(() => availableTabs.value.length > 0)

		watch(searchQuery, val => {
			if (debounceTimer) clearTimeout(debounceTimer)
			if (!val || val.length < 2) {
				searchResults.value = {}
				paginationMeta.value = {}
				isLoading.value = false
				return
			}
			isLoading.value = true
			debounceTimer = setTimeout(async () => {
				try {
					const translateFn = instance && instance.proxy ? instance.proxy.$t.bind(instance.proxy) : k => k
					const navItems = searchNavItems(val, translateFn)

					const { results, pagination } = await globalSearchAll(val)

					searchResults.value = {
						...(navItems.length ? { nav_modules: navItems } : {}),
						...results,
					}
					paginationMeta.value = pagination
				} catch (e) {
					console.error('Search error:', e)
					searchResults.value = {}
					paginationMeta.value = {}
				} finally {
					isLoading.value = false
				}
			}, 300)
		})

		const loadMore = async type => {
			const meta = paginationMeta.value[type]
			if (!meta || !meta.hasMore || loadingMore[type]) return

			loadingMore[type] = true
			try {
				const nextPage = meta.page + 1
				const { items, total, hasMore } = await loadMoreResults(type, searchQuery.value, nextPage)

				// Append items
				const current = searchResults.value[type] || []
				searchResults.value = {
					...searchResults.value,
					[type]: [...current, ...items],
				}
				paginationMeta.value = {
					...paginationMeta.value,
					[type]: { total, hasMore, page: nextPage },
				}
			} catch (e) {
				console.error('Load more error:', e)
			} finally {
				loadingMore[type] = false
			}
		}

		const openSearch = () => {
			shallShowFullSearchLocal.value = true
			nextTick(() => {
				if (searchInput.value) searchInput.value.focus()
			})
		}

		const closeSearch = () => {
			shallShowFullSearchLocal.value = false
			searchQuery.value = ''
			searchResults.value = {}
			paginationMeta.value = {}
			activeFilter.value = 'all'
		}

		watch(shallShowFullSearchLocal, value => {
			if (!value) {
				searchQuery.value = ''
				searchResults.value = {}
				paginationMeta.value = {}
				activeFilter.value = 'all'
			}
			store.commit('app/TOGGLE_CONTENT_OVERLAY', value)
			if (value) {
				setTimeout(() => {
					if (searchInput.value) searchInput.value.focus()
				}, 150)
			}
		})

		const navigateToItem = (type, item) => {
			const config = getTypeConfig(type)
			const route = config.getRoute(item)
			if (route) {
				closeSearch()
				router.push(route).catch(err => {
					if (err.name !== 'NavigationDuplicated') {
						console.error('Navigation error:', err, { type, route, item })
					}
				})
			}
		}

		return {
			shallShowFullSearchLocal,
			searchQuery,
			searchResults,
			paginationMeta,
			isLoading,
			loadingMore,
			activeFilter,
			searchInput,
			availableTabs,
			filteredResults,
			hasResults,
			getTypeConfig,
			openSearch,
			closeSearch,
			navigateToItem,
			loadMore,
			icons: { mdiMagnify, mdiClose, mdiChevronRight, mdiChevronDown },
		}
	},
}
</script>

<style lang="scss">
@import '~@core/preset/preset/mixins.scss';

@include theme(app-bar-autocomplete-box) using ($material) {
	div[role='combobox'] {
		background-color: map-deep-get($material, 'cards');
	}
	background-color: map-deep-get($material, 'cards');
}

.app-bar-autocomplete-box {
	position: absolute !important;
	width: 100%;
	top: 0;
	left: 0;
	border-radius: 14px !important;
	border-top-left-radius: 0 !important;
	border-top-right-radius: 0 !important;
	z-index: 10;

	.search-input-row {
		height: 64px;
		padding: 0 20px;
	}

	.search-native-input {
		flex: 1;
		border: none;
		outline: none;
		background: transparent;
		font-size: 16px;
		color: inherit;

		&::placeholder {
			opacity: 0.5;
		}
	}

	.search-results-dropdown {
		max-height: calc(100vh - 120px);
		overflow-y: auto;
		border-top: 1px solid rgba(0, 0, 0, 0.06);
		box-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);

		.filter-chips {
			display: flex;
			flex-wrap: wrap;
			border-bottom: 1px solid rgba(0, 0, 0, 0.06);
		}

		.result-group {
			& + .result-group {
				border-top: 1px solid rgba(0, 0, 0, 0.06);
			}
		}

		.result-group-header {
			font-size: 11px !important;
			letter-spacing: 0.5px;
			font-weight: 500;
		}

		.search-result-item {
			cursor: pointer;
			min-height: 48px;
			transition: background 0.15s ease;

			&:hover {
				background: rgba(0, 0, 0, 0.04);
			}

			&.has-route {
				&:hover {
					background: rgba(94, 86, 210, 0.08);
				}
			}

			.v-avatar {
				border-radius: 4px;
				overflow: hidden;
			}

			.source-chip {
				font-size: 9px !important;
				height: 18px !important;
				opacity: 0.6;
				text-transform: uppercase;
				letter-spacing: 0.5px;
			}
		}

		.load-more-row {
			display: flex;
			justify-content: center;
			padding: 4px 0 8px;
		}
	}
}

// Light mode - solid background
.theme--light {
	.app-bar-autocomplete-box {
		background-color: #fff;

		.search-results-dropdown {
			background-color: #fff;
		}
	}
}

// Dark mode - solid background + adjustments
.theme--dark {
	.app-bar-autocomplete-box {
		background-color: #312d4b;

		.search-results-dropdown {
			background-color: #312d4b;
		}
	}

	.search-results-dropdown {
		border-top-color: rgba(255, 255, 255, 0.08) !important;

		.filter-chips,
		.result-group + .result-group {
			border-color: rgba(255, 255, 255, 0.08) !important;
		}

		.search-result-item:hover {
			background: rgba(255, 255, 255, 0.04) !important;
		}
	}
}

// Expanded search in blurred appbar
.v-app-bar.bg-blur {
	.expanded-search {
		.app-bar-autocomplete-box {
			// Keep solid background even in blur mode
		}
	}
}
</style>
